/**
 * Billing feature hooks — pipeline, invoices list/detail, and invoice generation.
 *
 * Phase 12 / Task 12.3 — read-only dashboards over the API + ONE mutation
 * (Generate invoice). Mirrors packages/web-dispatch/src/features/fleet/hooks.ts.
 *
 * All endpoints are VIEW_INVOICES / CREATE_INVOICE-gated and tenant-scoped (RLS)
 * server-side — the API is the authoritative permission gate (three-layer
 * security). These hooks make NO trust decisions; the frontend gate is UX-only.
 *
 * Money is integer cents everywhere (invoices.ts / billable_events). `quantity`
 * is a Postgres `numeric` column → serialized as a string by postgres-js.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  apiErrorFromResponse,
  authedFetch,
  useApiQuery,
  useApiMutation,
  useApiOpts,
} from '@lodex-pro/web-shared';
import type { PaymentTermsBody, PaymentTermsResponse } from './payment-terms';
import type { CustomerIdentity } from '../../lib/customer-key';
import type { BillingPeriod } from '../../lib/billing-period';
import type { TicketEditBody } from './ticket-edit-form';
import type { RateSource } from '@lodex-pro/shared/rate-source';

// ─── Query keys ────────────────────────────────────────────────────────────────

export const BILLING_PIPELINE_KEY = ['billing', 'pipeline'] as const;
export const BILLING_INVOICES_KEY = ['billing', 'invoices'] as const;
/** Detail cache key is scoped by invoice id. */
export const billingInvoiceKey = (id: string): readonly string[] => ['billing', 'invoice', id];

// ─── Response row types (no `any` — strict) ──────────────────────────────────────

/** One customer's unbilled worklist from GET /v1/billing/pipeline.
 *  A row carries the customer under one of two identities (DEC-192 option A):
 *  `haulerOperatorId` (current model — every job the product creates today) or
 *  `operatorId` (legacy). Both null = an event with no customer at all. */
export interface PipelineOperator {
  /** Current-model customer id (DEC-192). */
  readonly haulerOperatorId: string | null;
  /** Legacy customer id. */
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly eventCount: number;
  readonly totalCents: number;
  /** #50 — tickets in this row billing has edited (DEC-356). */
  readonly editedTicketCount: number;
}
export interface PipelineResponse {
  readonly pipeline: readonly PipelineOperator[];
}

/** One row from GET /v1/billing/invoices. Cents fields are integer cents. */
export interface InvoiceSummary {
  readonly id: string;
  /** Legacy customer id (for linking only; never display it). */
  readonly operatorId: string | null;
  /** Current-model customer id (DEC-192; for linking only). */
  readonly haulerOperatorId: string | null;
  /** Resolved customer name (86bc7q0h3): hauler customer name, else platform operator
   *  display name/name, else legacy operator name. Null only if no identity resolves. */
  readonly operatorName: string | null;
  readonly invoiceNumber: string;
  readonly status: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly subtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly generatedAt: string;
}
export interface InvoicesResponse {
  readonly invoices: readonly InvoiceSummary[];
}

/** The invoice header from GET /v1/billing/invoices/:id. */
export interface InvoiceDetailHeader {
  readonly id: string;
  /** Legacy customer id (for linking only; never display it). */
  readonly operatorId: string | null;
  /** Current-model customer id (DEC-192; for linking only). */
  readonly haulerOperatorId: string | null;
  /** Resolved customer name (86bc7q0h3): hauler customer name, else platform operator
   *  display name/name, else legacy operator name. Null only if no identity resolves. */
  readonly operatorName: string | null;
  readonly invoiceNumber: string;
  readonly status: string;
  readonly grouping: string | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly subtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly generatedAt: string;
  readonly generatedBy: string | null;
  /** Lifecycle record (DEC-322). Timestamps are ISO instants; render local (DEC-004). */
  readonly sentAt: string | null;
  readonly paidAt: string | null;
  readonly paymentReference: string | null;
  /** The LOCAL calendar day the payment arrived, 'YYYY-MM-DD' — a date, not an instant,
   *  so it is displayed as-is and never run through a timezone conversion. */
  readonly paymentDate: string | null;
  readonly voidAt: string | null;
  readonly voidReason: string | null;
  readonly voidedByName: string | null;
  /** DEC-326: fixed when the invoice is SENT; null on a draft. */
  readonly paymentTermsDays: number | null;
  /** DEC-326: tenant-local calendar date, YYYY-MM-DD; null on a draft. */
  readonly dueDate: string | null;
}
/** One invoice line item. `quantity` is a numeric column → string over the wire. */
export interface InvoiceLineItem {
  readonly jobId: string | null;
  /** Gap 6 (0250) — the day the line is billed on, 'YYYY-MM-DD'; null before 0250. */
  readonly serviceDate?: string | null;
  readonly lineType: string;
  readonly quantity: string;
  readonly unitLabel: string | null;
  readonly rateCents: number;
  readonly amountCents: number;
  readonly sortOrder: number;
}
/** A ticket (job) covered by the invoice. */
export interface InvoiceJobRef {
  readonly jobId: string;
}
export interface InvoiceDetailResponse {
  readonly invoice: InvoiceDetailHeader;
  readonly lineItems: readonly InvoiceLineItem[];
  readonly jobs: readonly InvoiceJobRef[];
}

/** The customer an invoice is generated for — exactly one identity (DEC-192 option A;
 *  the API rejects both or neither with a 400). */
export type GenerateInvoiceCustomer = CustomerIdentity;
/** Body for POST /v1/billing/invoices/generate. */
export type GenerateInvoiceBody = GenerateInvoiceCustomer & {
  readonly periodStart: string;
  readonly periodEnd: string;
};
/** Response from POST /v1/billing/invoices/generate. */
export interface GenerateInvoiceResponse {
  readonly ok: boolean;
  readonly created: boolean;
  readonly invoiceId?: string;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────────

/** Unbilled worklist grouped by operator (event count + total cents). */
export function useBillingPipeline(): ReturnType<typeof useApiQuery<PipelineResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PipelineResponse>(opts, '/v1/billing/pipeline', {
    queryKey: BILLING_PIPELINE_KEY,
    staleTime: 30_000,
  });
}

/** All invoices for the active tenant (most-recent first). */
export function useInvoices(): ReturnType<typeof useApiQuery<InvoicesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<InvoicesResponse>(opts, '/v1/billing/invoices', {
    queryKey: BILLING_INVOICES_KEY,
    staleTime: 30_000,
  });
}

/** A single invoice with its line items + covered jobs. Disabled until `id` set. */
export function useInvoiceDetail(
  id: string | null,
): ReturnType<typeof useApiQuery<InvoiceDetailResponse>> {
  const opts = useApiOpts();
  return useApiQuery<InvoiceDetailResponse>(opts, `/v1/billing/invoices/${id ?? ''}`, {
    queryKey: billingInvoiceKey(id ?? ''),
    staleTime: 30_000,
    enabled: id !== null && id !== '',
  });
}

// ─── Aging (AR) ──────────────────────────────────────────────────────────────────

export const BILLING_AGING_KEY = ['billing', 'aging'] as const;

/** One operator's aging buckets from GET /v1/billing/aging. All values are cents. */
export interface AgingOperator {
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly current: number;
  readonly d31_60: number;
  readonly d61_90: number;
  readonly d90plus: number;
  readonly total: number;
}
/** Tenant-wide aging totals (sum across operators), cents. */
export interface AgingTotals {
  readonly current: number;
  readonly d31_60: number;
  readonly d61_90: number;
  readonly d90plus: number;
  readonly total: number;
}
export interface AgingResponse {
  /** The IANA tz the server bucketed by (from users.timezone) — shown for transparency. */
  readonly timezone: string;
  readonly operators: readonly AgingOperator[];
  readonly totals: AgingTotals;
}

/**
 * SENT + unpaid invoices aged into current / 31-60 / 61-90 / 90+ buckets per operator
 * (+ totals). Bucketed by the user's LOCAL day server-side (DEC-066); read-only.
 */
export function useAging(): ReturnType<typeof useApiQuery<AgingResponse>> {
  const opts = useApiOpts();
  return useApiQuery<AgingResponse>(opts, '/v1/billing/aging', {
    queryKey: BILLING_AGING_KEY,
    staleTime: 30_000,
  });
}

// ─── Mutation ────────────────────────────────────────────────────────────────────

/**
 * Generate one invoice from an operator's UNBILLED billable_events for a period.
 * On success the operator's tickets move TO_BE_INVOICED→INVOICED and disappear
 * from the pipeline, so we invalidate BOTH the pipeline and invoices caches.
 *
 * @param onDone  Called after a successful generation (e.g. toast + close).
 */
/** The three invoice lifecycle actions (86bc83kk9), as their URL segments. */
export type InvoiceAction = 'send' | 'mark-paid' | 'void';

/** Response from POST /v1/billing/invoices/:id/{send,mark-paid,void}. */
export interface InvoiceTransitionResponse {
  readonly ok: boolean;
  readonly id: string;
  readonly status: string;
  readonly previousStatus: string;
  /** Void only (DEC-322): the work handed back to the Pipeline. */
  readonly released?: { readonly eventCount: number; readonly ticketCount: number };
}

/**
 * The body each action takes. `send` takes no fields, but its body must still be an EMPTY
 * OBJECT rather than `undefined`: calling `mutate(undefined)` on a POST sends no body at
 * all while keeping the JSON content-type, which Fastify rejects as a malformed body —
 * surfacing to the user as a 500 from an endpoint that is working perfectly.
 *
 * DEC-322: void requires a reason (5+ characters); mark-paid requires the payment
 * reference and the LOCAL calendar day the payment arrived, as 'YYYY-MM-DD'.
 */
export interface InvoiceTransitionBodies {
  readonly send: Record<string, never>;
  readonly 'mark-paid': { readonly paymentReference: string; readonly paidDate: string };
  readonly void: { readonly reason: string };
}

/**
 * Move one invoice along its lifecycle: DRAFT →send→ SENT →mark-paid→ PAID, or
 * DRAFT|SENT →void→ VOID. The server owns the legal graph and answers 409
 * INVOICE_TRANSITION_FORBIDDEN for anything else; this hook makes no trust decision.
 *
 * Invalidates the detail, the list, the Pipeline AND the aging report — aging is the surface that was
 * previously unreachable, so leaving it stale would hide the one result that matters.
 */
export function useInvoiceTransition<A extends InvoiceAction>(
  id: string | null,
  action: A,
  onDone: (result: InvoiceTransitionResponse) => void,
): ReturnType<typeof useApiMutation<InvoiceTransitionBodies[A], InvoiceTransitionResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<InvoiceTransitionBodies[A], InvoiceTransitionResponse>(
    opts,
    `/v1/billing/invoices/${id ?? ''}/${action}`,
    'POST',
    {
      onSuccess: (result) => {
        void qc.invalidateQueries({ queryKey: billingInvoiceKey(id ?? '') });
        void qc.invalidateQueries({ queryKey: BILLING_INVOICES_KEY });
        void qc.invalidateQueries({ queryKey: BILLING_AGING_KEY });
        // A void hands its work back to the Pipeline (DEC-322), so that list changes too,
        // and its tickets become editable again.
        void qc.invalidateQueries({ queryKey: BILLING_PIPELINE_KEY });
        void qc.invalidateQueries({ queryKey: BILLING_TICKET_KEY });
        onDone(result);
      },
    },
  );
}

export function useGenerateInvoice(
  onDone: (result: GenerateInvoiceResponse) => void,
): ReturnType<typeof useApiMutation<GenerateInvoiceBody, GenerateInvoiceResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<GenerateInvoiceBody, GenerateInvoiceResponse>(
    opts,
    '/v1/billing/invoices/generate',
    'POST',
    {
      onSuccess: (result) => {
        // The Pipeline key prefixes the drill-down's, so this refreshes both.
        void qc.invalidateQueries({ queryKey: BILLING_PIPELINE_KEY });
        void qc.invalidateQueries({ queryKey: BILLING_INVOICES_KEY });
        // Generated tickets are now on an invoice, which locks them.
        void qc.invalidateQueries({ queryKey: BILLING_TICKET_KEY });
        onDone(result);
      },
    },
  );
}

// ─── Payment terms (DEC-326 item 7) ─────────────────────────────────────────────

export const BILLING_PAYMENT_TERMS_KEY = ['billing', 'payment-terms'] as const;

export function usePaymentTerms(): ReturnType<typeof useApiQuery<PaymentTermsResponse>> {
  const opts = useApiOpts();
  return useApiQuery<PaymentTermsResponse>(opts, '/v1/billing/payment-terms', {
    queryKey: BILLING_PAYMENT_TERMS_KEY,
    staleTime: 30_000,
  });
}

export function useSavePaymentTerms(
  onDone: (result: PaymentTermsResponse) => void,
): ReturnType<typeof useApiMutation<PaymentTermsBody, PaymentTermsResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<PaymentTermsBody, PaymentTermsResponse>(
    opts,
    '/v1/billing/payment-terms',
    'PUT',
    {
      onSuccess: (result) => {
        qc.setQueryData(BILLING_PAYMENT_TERMS_KEY, result);
        onDone(result);
      },
    },
  );
}

// ─── Pipeline drill-down (#50 PR A) ─────────────────────────────────────────────

/** Every ticket page; generating or voiding invalidates them all. */
export const BILLING_TICKET_KEY = ['billing', 'ticket'] as const;
export const billingTicketKey = (jobId: string): readonly string[] => [
  ...BILLING_TICKET_KEY,
  jobId,
];

/** One ticket row from GET /v1/billing/pipeline/tickets. Quantities are decimal strings. */
export interface PipelineTicket {
  readonly jobId: string;
  readonly haulId: string | null;
  readonly status: string;
  readonly completedAt: string | null;
  readonly siteName: string | null;
  readonly jobType: string | null;
  readonly billingMethod: string | null;
  readonly driverName: string | null;
  readonly volumeBbl: string | null;
  readonly hours: string | null;
  readonly extrasCount: number;
  /** Unbilled lines counted in this row (the period's, or all of them with `all`). */
  readonly eventCount: number;
  /** Of the ticket's unbilled lines, how many Generate would bill for the month. */
  readonly eventsInPeriod: number;
  readonly totalCents: number;
  /** Billing edited this ticket (DEC-356). */
  readonly edited: boolean;
}
export interface PipelineTicketsResponse {
  readonly customer: {
    readonly haulerOperatorId: string | null;
    readonly operatorId: string | null;
    readonly operatorName: string | null;
  };
  /** What Generate would bill for the month, pre-tax. */
  readonly period: { readonly ticketCount: number; readonly totalCents: number };
  readonly allUnbilled: { readonly ticketCount: number; readonly totalCents: number };
  /** Unbilled lines with no ticket; read-only. */
  readonly otherCharges: { readonly eventCount: number; readonly totalCents: number };
  readonly tickets: readonly PipelineTicket[];
  readonly nextCursor: string | null;
}

/**
 * One customer's unbilled tickets, page by page. The endpoint is keyset-paged, so this is
 * an infinite query: the page shows "Load more" instead of silently stopping at page 1.
 * The key sits under BILLING_PIPELINE_KEY, so Generate refreshes it with the Pipeline.
 */
export function usePipelineTickets(
  customer: CustomerIdentity | null,
  period: BillingPeriod | null,
  includeAll: boolean,
): UseInfiniteQueryResult<InfiniteData<PipelineTicketsResponse>> {
  const opts = useApiOpts();
  const idParam =
    customer === null
      ? ''
      : 'haulerOperatorId' in customer
        ? `haulerOperatorId=${customer.haulerOperatorId}`
        : `operatorId=${customer.operatorId}`;
  return useInfiniteQuery({
    queryKey: [...BILLING_PIPELINE_KEY, 'tickets', idParam, period?.value ?? '', includeAll],
    enabled: customer !== null && period !== null,
    staleTime: 30_000,
    initialPageParam: null as string | null,
    getNextPageParam: (last: PipelineTicketsResponse) => last.nextCursor,
    queryFn: async ({ pageParam }): Promise<PipelineTicketsResponse> => {
      const qs = new URLSearchParams(idParam);
      qs.set('periodStart', period?.periodStart ?? '');
      qs.set('periodEnd', period?.periodEnd ?? '');
      if (includeAll) qs.set('all', 'true');
      if (pageParam !== null) qs.set('cursor', pageParam);
      const path = `/v1/billing/pipeline/tickets?${qs.toString()}`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        throw await apiErrorFromResponse(res, path);
      }
      return res.json() as Promise<PipelineTicketsResponse>;
    },
  });
}

/** What a billed line is (api services/billing/ticket-page.ts). */
export type TicketLineKind =
  | 'VOLUME'
  | 'HOURS'
  | 'LOAD'
  | 'SERVICE_CHARGE'
  | 'LINE_ITEM'
  /** #52 / DEC-358 — the disposal fee. */
  | 'DISPOSAL_FEE'
  | 'OTHER';

/** Why billing can't edit the ticket; null when it can. */
export type TicketEditLock =
  | { readonly code: 'NOT_TO_BE_INVOICED'; readonly status: string }
  | {
      readonly code: 'ON_INVOICE';
      readonly invoiceId: string;
      readonly invoiceNumber: string;
      readonly invoiceStatus: string;
    }
  | { readonly code: 'NO_UNBILLED_LINES' };

export interface TicketLine {
  readonly eventId: string;
  readonly kind: TicketLineKind;
  readonly billingMethod: string;
  readonly description: string;
  readonly unitLabel: string;
  /** Decimal string, exactly as stored. */
  readonly quantity: string;
  /** Integer cents; null = needs pricing (bills $0). */
  readonly rateCents: number | null;
  /**
   * Gap 1 (D-15) — where the primary line's rate came from, as approve stored it; null on
   * every other line and on lines approved before 0271. Billing ticket page only: never an
   * invoice, PDF or export.
   */
  readonly rateSource: RateSource | null;
  readonly needsPricing: boolean;
  readonly amountCents: number;
  readonly createdAt: string | null;
  readonly invoice: {
    readonly id: string;
    readonly number: string | null;
    readonly status: string | null;
  } | null;
}

export interface TicketTank {
  readonly sizeType: string | null;
  readonly tankNo: string | null;
  readonly topGaugeFeet: number | null;
  readonly topGaugeInches: number | null;
  readonly bottomGaugeFeet: number | null;
  readonly bottomGaugeInches: number | null;
  readonly colorCut: string | null;
  readonly waterBbl: string | null;
  readonly oilBbl: string | null;
}

/** GET /v1/billing/tickets/:jobId. Instants are ISO; render them local (DEC-004). */
export interface TicketPageResponse {
  readonly ticket: {
    readonly jobId: string;
    readonly haulId: string | null;
    readonly status: string;
    readonly completedAt: string | null;
    /** Gap 6 (0250) — the day this ticket is billed on, 'YYYY-MM-DD' (the hauler's "Bill on"). */
    readonly serviceDate?: string | null;
    readonly jobType: string | null;
    readonly billingMethod: string | null;
    readonly product: string | null;
  };
  readonly customer: {
    readonly haulerOperatorId: string | null;
    readonly operatorId: string | null;
    readonly operatorName: string | null;
  };
  readonly site: {
    readonly lease: string | null;
    readonly county: string | null;
    readonly countyState: string | null;
    readonly legals: string | null;
    readonly disposal: string | null;
  };
  readonly tanks: readonly TicketTank[];
  readonly capture: {
    readonly captureCount: number;
    readonly captureMethod: string | null;
    readonly capturedNetBbl: string | null;
    readonly reviewedNetBbl: string | null;
  };
  readonly times: {
    readonly startTime: string | null;
    readonly jobStart: string | null;
    readonly jobEnd: string | null;
    readonly disposalIn: string | null;
    readonly disposalOut: string | null;
    readonly endTime: string | null;
  };
  readonly driverNotes: string | null;
  readonly crew: {
    readonly driverName: string | null;
    /** OPEN-157 — "Signed by <signer>, corrected to <driver>" after a driver correction. */
    readonly driverLabel?: string | null;
    /** 0238 — "Hours move pending until <driver>'s clock stops" while a correction waits. */
    readonly hoursMovePending?: string | null;
    readonly unitNumber: string | null;
    readonly trailerNumber: string | null;
  };
  readonly lines: readonly TicketLine[];
  readonly unbilledTotalCents: number;
  readonly editable: boolean;
  readonly lock: TicketEditLock | null;
  /** OPEN-156 — approved and on no live invoice, so billing may void it. */
  readonly voidable: boolean;
  /** Set once the ticket (or its load, DEC-355) is voided. at is ISO (DEC-004). */
  readonly voided: {
    readonly at: string | null;
    readonly reason: string | null;
    readonly byName: string | null;
  } | null;
  /** #50 PR C — billing's edits, served beside the read model (never on the invoice). */
  readonly lineEdits: readonly TicketLineEdit[];
  readonly history: readonly TicketEditHistoryEntry[];
  /**
   * #52 (DEC-358 #9) — the disposal fee "Add disposal fee" would add (a ticket approved before
   * fees were switched on); null when the ticket takes none or already has one. Optional so an
   * older API reads as "no offer".
   */
  readonly disposalFeeOffer?: DisposalFeeOffer | null;
  /**
   * OPEN-217 (DEC-382) — the Warnings the driver confirmed on the phone, served beside the read
   * model (never on the invoice). Optional so an older API reads as none.
   */
  readonly driverConfirmedWarnings?: readonly DriverConfirmedWarning[];
}

/** A Warning the driver saw and confirmed; `message` is what they read, value filled in. */
export interface DriverConfirmedWarning {
  readonly ruleId: string;
  readonly field: string;
  readonly stage: string;
  readonly position: number | null;
  readonly serviceChargeId: string | null;
  readonly value: number | null;
  readonly message: string;
  readonly confirmedAt: string;
}

export interface DisposalFeeOffer {
  readonly site: {
    readonly kind: 'FACILITY' | 'LOCATION' | 'WELL';
    readonly id: string;
    readonly name: string;
  };
  /** The company-local day whose fee applies, 'YYYY-MM-DD'. */
  readonly day: string;
  /** null = no fee is set (set it first); 0 = a deliberate $0.00, nothing to add. */
  readonly feeCentsPerBbl: number | null;
  readonly source: 'CUSTOMER' | 'DEFAULT' | 'NONE';
  readonly haulerOperatorId: string | null;
  /** The billed volume the line would carry. */
  readonly quantity: string;
}

/** A billed line billing touched: the quantity before the first edit, or added by billing. */
export interface TicketLineEdit {
  readonly eventId: string;
  readonly originalQuantity: string | null;
  readonly adjustedAt: string | null;
  readonly addedByBilling: boolean;
}

/** One change inside a save, as the BILLING_TICKET_EDITED audit row recorded it. */
export interface TicketEditChange {
  readonly op: 'UPDATE' | 'ADD' | 'REMOVE';
  readonly field: 'net_bbl' | 'hours' | 'service_charge' | 'line_item' | 'disposal_fee';
  readonly label: string | null;
  readonly before: { readonly quantity: string; readonly amountCents: number } | null;
  readonly after: { readonly quantity: string; readonly amountCents: number } | null;
  readonly reviewedNetBbl?: { readonly before: string | null; readonly after: string };
}

/** One save in the ticket's edit history (newest first). occurredAt is ISO (DEC-004). */
export interface TicketEditHistoryEntry {
  readonly occurredAt: string | null;
  readonly actorName: string | null;
  readonly reason: string | null;
  readonly changes: readonly TicketEditChange[] | null;
  readonly ticketTotalCents: { readonly before: number; readonly after: number } | null;
}

/** One ticket as billing sees it. Disabled until `jobId` is set. */
export function useBillingTicket(
  jobId: string | null,
): ReturnType<typeof useApiQuery<TicketPageResponse>> {
  const opts = useApiOpts();
  return useApiQuery<TicketPageResponse>(opts, `/v1/billing/tickets/${jobId ?? ''}`, {
    queryKey: billingTicketKey(jobId ?? ''),
    staleTime: 30_000,
    enabled: jobId !== null && jobId !== '',
  });
}

// ─── Invoice PDF (#50 PR D) ─────────────────────────────────────────────────────

/** GET /v1/billing/invoices/:id/pdf — a short-lived presigned URL to the rendered PDF. */
export interface InvoicePdfLink {
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly filename: string;
  readonly url: string;
  readonly expiresInSeconds: number;
}

/**
 * Render the invoice (summary + one backup page per ticket) and get a link to it. A GET
 * triggered by a click, not on mount: rendering costs a Lambda run, so it happens only when
 * someone asks for the PDF. Errors carry the API code (INVOICE_PDF_UNAVAILABLE).
 */
export function useInvoicePdf(id: string): UseMutationResult<InvoicePdfLink, Error, void> {
  const opts = useApiOpts();
  return useMutation<InvoicePdfLink, Error, void>({
    mutationFn: async (): Promise<InvoicePdfLink> => {
      const path = `/v1/billing/invoices/${id}/pdf`;
      const res = await authedFetch(opts, path);
      if (!res.ok) {
        throw await apiErrorFromResponse(res, path);
      }
      return (await res.json()) as InvoicePdfLink;
    },
  });
}

// ─── Ticket edit (#50 PR C, DEC-356) ────────────────────────────────────────────

/** Response of PATCH /v1/billing/tickets/:jobId. */
export interface TicketEditResponse {
  readonly ok: boolean;
  readonly ticketTotalCents: { readonly before: number; readonly after: number };
}

/**
 * Save billing's edit of an approved ticket. On success the ticket page, the drill-down
 * and the Pipeline all change (totals, Edited badges), so all three are invalidated.
 */
export function useEditBillingTicket(
  jobId: string,
  onDone: (result: TicketEditResponse) => void,
): ReturnType<typeof useApiMutation<TicketEditBody, TicketEditResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TicketEditBody, TicketEditResponse>(
    opts,
    `/v1/billing/tickets/${jobId}`,
    'PATCH',
    {
      onSuccess: (result) => {
        void qc.invalidateQueries({ queryKey: billingTicketKey(jobId) });
        void qc.invalidateQueries({ queryKey: BILLING_PIPELINE_KEY });
        onDone(result);
      },
    },
  );
}

/** POST /v1/billing/tickets/:jobId/void (OPEN-156). */
export interface TicketVoidResponse {
  readonly ok: true;
  readonly jobId: string;
  readonly status: string;
  readonly removedLines: number;
  readonly removedTotalCents: string;
}

export function useVoidBillingTicket(
  jobId: string,
  onDone: (result: TicketVoidResponse) => void,
): ReturnType<typeof useApiMutation<{ reason: string }, TicketVoidResponse>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, TicketVoidResponse>(
    opts,
    `/v1/billing/tickets/${jobId}/void`,
    'POST',
    {
      onSuccess: (result) => {
        void qc.invalidateQueries({ queryKey: billingTicketKey(jobId) });
        void qc.invalidateQueries({ queryKey: BILLING_PIPELINE_KEY });
        onDone(result);
      },
    },
  );
}

/** An active catalog charge, for "Add extra". */
export interface ServiceChargeOption {
  readonly id: string;
  readonly label: string;
  readonly unitOfMeasure: string;
  readonly defaultRateCents: number;
}

/**
 * Active service charges matching `q` (all of them when q is empty, up to the API's 200).
 * The picker narrows by typing, so a long catalog is searched, not silently cut.
 */
export function useServiceChargeSearch(
  q: string,
): ReturnType<typeof useApiQuery<{ readonly serviceCharges: readonly ServiceChargeOption[] }>> {
  const opts = useApiOpts();
  const term = q.trim();
  const qs = new URLSearchParams({ limit: '200' });
  if (term !== '') qs.set('q', term);
  return useApiQuery<{ readonly serviceCharges: readonly ServiceChargeOption[] }>(
    opts,
    `/v1/service-charges?${qs.toString()}`,
    { queryKey: ['billing', 'service-charges', term], staleTime: 60_000 },
  );
}
